import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { getAllBubbleData } from "../services/api/NotificationsApi";
import { useAuth } from "./AuthContext";

const BubbleSettingsContext = createContext();

export const BubbleSettingsProvider = ({ children }) => {
  const { user } = useAuth(); // Get login status
  const [bubbleSettings, setBubbleSettings] = useState(() => {
    const stored = localStorage.getItem("bubbleSettings");
    return stored ? JSON.parse(stored) : null;
  });

  // Use a ref to track execution to prevent race conditions
  const isFetching = useRef(false);

  const fetchAndUpdateBubbleSettings = async () => {
    // Safety: Do not fetch if no user is logged in
    if (!user) return;

    try {
      isFetching.current = true;
      const bubbleRes = await getAllBubbleData();
      localStorage.setItem("bubbleSettings", JSON.stringify(bubbleRes.data));
      setBubbleSettings(bubbleRes.data);
    } catch (error) {
      console.error("Failed to fetch bubble settings:", error);
    } finally {
      isFetching.current = false;
    }
  };

  // 1. Initial Fetch on Login
  useEffect(() => {
    if (user) {
      fetchAndUpdateBubbleSettings();
    }
  }, [user]);

  // 2. Real-time Polling Logic
  useEffect(() => {
    if (!user) return; // Stop if not logged in

    const handleVisibilityChange = () => {
      // Immediately fetch when user returns to the tab
      if (document.visibilityState === 'visible') {
        fetchAndUpdateBubbleSettings();
      }
    };

    // Set up the 20-second interval
    const intervalId = setInterval(() => {
      // Only fetch if the window is currently visible to the user
      if (document.visibilityState === 'visible') {
        fetchAndUpdateBubbleSettings();
      }
    }, 20000); 

    // Listen for tab switching
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Cleanup
    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [user]);

  return (
    <BubbleSettingsContext.Provider
      value={{ bubbleSettings, setBubbleSettings, fetchAndUpdateBubbleSettings }}
    >
      {children}
    </BubbleSettingsContext.Provider>
  );
};

export const useBubbleSettings = () => useContext(BubbleSettingsContext);